import React from "react";
import { AuthProvider, EspType } from "@/types/mailbox.types";
import { Mail } from "lucide-react";

// Helper function to create image element with fallback
const createIconImage = (src: string, alt: string, fallback = "/mailIcon.png") => {
  return <img
    src={src}
    alt={alt}
    className="!size-5"
    onError={(e) => {
      const target = e.target as HTMLImageElement;
      if (target.src !== fallback) {
        target.src = fallback;
      }
    }}
  />;
};

// Common icon mapping used for both AuthProvider enum and EspType enum/strings
const iconMappings: Record<string, { icon: React.ReactElement; tooltip: string }> = {
  // AWS related
  [AuthProvider.AWS_SMTP]: {
    icon: <img src="/amazonIcon.png" alt="aws icon" className="!size-5" />,
    tooltip: "Amazon SES SMTP",
  },
  [AuthProvider.AWS_API]: {
    icon: <img src="/amazonIcon.png" alt="aws icon" className="!size-5" />,
    tooltip: "Amazon SES",
  },
  "amazon_ses": {
    icon: <img src="/amazonIcon.png" alt="aws icon" className="!size-5" />,
    tooltip: "Amazon SES",
  },
  "aws_ses": {
    icon: <img src="/amazonIcon.png" alt="aws icon" className="!size-5" />,
    tooltip: "AWS SES",
  },
  
  // SendGrid related
  [AuthProvider.SENDGRID_SMTP]: {
    icon: (
      <img src="/sendGridIcon.png" alt="sendgrid icon" className="!size-5" />
    ),
    tooltip: "SendGrid SMTP",
  },
  "sendgrid": {
    icon: (
      <img src="/sendGridIcon.png" alt="sendgrid icon" className="!size-5" />
    ),
    tooltip: "SendGrid",
  },
  [AuthProvider.SENDGRID_API]: {
    icon: (
      <img src="/sendGridIcon.png" alt="sendgrid icon" className="!size-5" />
    ),
    tooltip: "SendGrid",
  },
  
  // Private SMTP
  [AuthProvider.PRIVATE]: {
    icon: <Mail className="!size-5" />,
    tooltip: "Private SMTP",
  },
  "imap": {
    icon: <Mail className="!size-5" />,
    tooltip: "Private SMTP",
  },
  
  // Google related
  [AuthProvider.GOOGLE]: {
    icon: <img src="/googleIcon.png" alt="google icon" className="!size-5" />,
    tooltip: "Google Workspace",
  },
  "gmail": {
    icon: <img src="/providers-logos/gmail.png" alt="gmail icon" className="!size-5" />,
    tooltip: "Gmail",
  },
  "google_workspace": {
    icon: <img src="/providers-logos/google-workspace.png" alt="google icon" className="!size-5" />,
    tooltip: "Google Workspace",
  },
  "google_workspace_smtp": {
    icon: <img src="/providers-logos/google-workspace.png" alt="google icon" className="!size-5" />,
    tooltip: "Google Workspace",
  },
  
  // Microsoft related
  [AuthProvider.MICROSOFT]: {
    icon: (
      <img src="/microsoftIcon.png" alt="microsoft icon" className="!size-5" />
    ),
    tooltip: "Microsoft",
  },
  "outlook": {
    icon: <img src="/providers-logos/outlook.png" alt="outlook icon" className="!size-5" />,
    tooltip: "Outlook",
  },
  "microsoft_365": {
    icon: <img src="/providers-logos/microsoft-365.png" alt="microsoft icon" className="!size-5" />,
    tooltip: "Microsoft 365",
  },
  "microsoft_office365": {
    icon: <img src="/providers-logos/microsoft-365.png" alt="microsoft icon" className="!size-5" />,
    tooltip: "Microsoft Office 365",
  },
  "Microsoft365": {
    icon: <img src="/providers-logos/microsoft-365.png" alt="microsoft icon" className="!size-5" />,
    tooltip: "Microsoft 365",
  },
  "microsoft365": {
    icon: <img src="/providers-logos/microsoft-365.png" alt="microsoft icon" className="!size-5" />,
    tooltip: "Microsoft 365",
  },
  "MS365": {
    icon: <img src="/providers-logos/microsoft-365.png" alt="microsoft icon" className="!size-5" />,
    tooltip: "Microsoft 365",
  },
  "ms365": {
    icon: <img src="/providers-logos/microsoft-365.png" alt="microsoft icon" className="!size-5" />,
    tooltip: "Microsoft 365",
  },
  "Microsoft 365": {
    icon: <img src="/providers-logos/microsoft-365.png" alt="microsoft icon" className="!size-5" />,
    tooltip: "Microsoft 365",
  },
  
  // Additional ESP types from B2C Sender API
  "mailgun": {
    icon: <img src="/mailgun.png" alt="mailgun icon" className="!size-5" />,
    tooltip: "Mailgun",
  },
  "postmark": {
    icon: <img src="/postmark.png" alt="postmark icon" className="!size-5" />,
    tooltip: "Postmark",
  },
  "sparkpost": {
    icon: <img src="/sparkpost.jpeg" alt="sparkpost icon" className="!size-5" />,
    tooltip: "SparkPost",
  },
  "mailjet": {
    icon: <img src="/mailjet.png" alt="mailjet icon" className="!size-5" />,
    tooltip: "Mailjet",
  },
  "yahoo": {
    icon: <img src="/providers-logos/yahoo.png" alt="yahoo icon" className="!size-5" />,
    tooltip: "Yahoo",
  },
  "aol": {
    icon: <img src="/providers-logos/aol.png" alt="aol icon" className="!size-5" />,
    tooltip: "AOL",
  },
  "proton": {
    icon: <img src="/providers-logos/proton-mail.png" alt="proton icon" className="!size-5" />,
    tooltip: "Proton Mail",
  },
  "zoho": {
    icon: <img src="/providers-logos/zoho-mail.png" alt="zoho icon" className="!size-5" />,
    tooltip: "Zoho Mail",
  },
  "inbox": {
    icon: <Mail className="!size-5" />,
    tooltip: "Inbox",
  },
  "fastmail": {
    icon: <img src="/fastmail.png" alt="fastmail icon" className="!size-5" />,
    tooltip: "Fastmail",
  },
  "yandex": {
    icon: <img src="/yandex.png" alt="yandex icon" className="!size-5" />,
    tooltip: "Yandex",
  },
  "other": {
    icon: <Mail className="!size-5" />,
    tooltip: "Other",
  },

  // Seed ESP Types
  "klaviyo": {
    icon: <img src="/providers-logos/klaviyo.png" alt="klaviyo icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Klaviyo",
  },
  "mailchimp": {
    icon: <img src="/providers-logos/mailchimp.png" alt="mailchimp icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Mailchimp",
  },
  "shopify_email": {
    icon: <img src="/providers-logos/shopify-email.png" alt="shopify email icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Shopify Email",
  },
  "shopify": {
    icon: <img src="/providers-logos/shopify-email.png" alt="shopify icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Shopify Email",
  },
  "activecampaign": {
    icon: <img src="/providers-logos/activecampaign.png" alt="activecampaign icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "ActiveCampaign",
  },
  "brevo": {
    icon: <img src="/providers-logos/brevo.png" alt="brevo icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Brevo (Sendinblue)",
  },
  "sendinblue": {
    icon: <img src="/providers-logos/brevo.png" alt="sendinblue icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Brevo (Sendinblue)",
  },
  "convertkit": {
    icon: <img src="/providers-logos/convertkit.png" alt="convertkit icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "ConvertKit",
  },
  "hubspot": {
    icon: <img src="/providers-logos/hubspot.png" alt="hubspot icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "HubSpot",
  },
  "drip": {
    icon: <img src="/providers-logos/drip.png" alt="drip icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Drip",
  },
  "getresponse": {
    icon: <img src="/providers-logos/getresponse.png" alt="getresponse icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "GetResponse",
  },
  "moosend": {
    icon: <img src="/providers-logos/moosend.png" alt="moosend icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Moosend",
  },
  "campaign_monitor": {
    icon: <img src="/providers-logos/campaign-monitor.png" alt="campaign monitor icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Campaign Monitor",
  },
  "zoho_campaigns": {
    icon: <img src="/providers-logos/zoho-campaigns.png" alt="zoho campaigns icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Zoho Campaigns",
  },
  "netcore_cloud": {
    icon: <img src="/providers-logos/netcore-cloud.png" alt="netcore cloud icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Netcore Cloud",
  },
  "netcore": {
    icon: <img src="/providers-logos/netcore-cloud.png" alt="netcore icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Netcore Cloud",
  },
  "mailmodo": {
    icon: <img src="/providers-logos/mailmodo.png" alt="mailmodo icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Mailmodo",
  },
  "mailerlite": {
    icon: <img src="/providers-logos/mailerlite.png" alt="mailerlite icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "MailerLite",
  },
  "constant_contact": {
    icon: <img src="/providers-logos/constant-contact.png" alt="constant contact icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Constant Contact",
  },
  "omnisend": {
    icon: <img src="/providers-logos/omnisend.png" alt="omnisend icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Omnisend",
  },
  "aweber": {
    icon: <img src="/providers-logos/aweber.png" alt="aweber icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "AWeber",
  },
  "azure":{
    icon: <img src="/azure.jpeg" alt="azure icon" className="!size-5" onError={(e) => { (e.target as HTMLImageElement).src = '/mailIcon.png'; }} />,
    tooltip: "Azure",
  }

};

export const authProviderIcons = iconMappings;
